import { Card } from 'antd';
import { QRCodeSVG } from 'qrcode.react';
import { space } from '@viq-admin/ui';

/**
 * Maximum payload a QR symbol can carry: version 40 at error-correction
 * level M. `qrcode.react` throws past this rather than degrading, and an
 * uncaught throw here would take down the entire lead-detail screen.
 *
 * This is the byte-MODE capacity, which is the most conservative of the
 * three modes the encoder can pick — it is not the true capacity for every
 * payload. Numeric-only payloads get numeric mode (capacity 5596 bytes) and
 * payloads drawn from the QR alphanumeric set (`0-9 A-Z space $%*+-./:`) get
 * alphanumeric mode (capacity 3391 bytes) — and badge payloads like
 * `213332$AUTO0626$David$Howerton$VendoIQ$` are exactly that alphanumeric
 * shape. So a payload that would still encode fine in numeric or
 * alphanumeric mode, but sits above this byte-mode bound, renders nothing
 * here — indistinguishable from "no code scanned" rather than "code too
 * long". That tradeoff is accepted deliberately: computing the real,
 * mode-dependent capacity would require re-implementing (or importing)
 * qrcode.react's mode-selection logic just to decide whether to render, for
 * a panel whose failure mode is already "renders nothing" either way. A
 * single conservative constant that can never throw is worth more here than
 * exact per-payload capacity.
 */
const QR_MAX_BYTES = 2331;

const textEncoder = new TextEncoder();

export interface BadgeQrPanelProps {
  /** Raw decoded QR payload from `persons.card_qr`. */
  cardQr?: string | null;
}

/**
 * The lead's scanned QR, re-rendered from its decoded payload.
 *
 * Sibling of CardImagesPanel rather than a tile inside it: that panel is
 * titled "Card images" and returns null when the shell supplies no image
 * API, which is how card images stay out of the customer portal. Nesting
 * here would force the portal to render a card called "Card images" whose
 * only content is not an image.
 *
 * Renders NOTHING — no card, no title, no frame — when there is no usable
 * payload, so neither shell ever shows an empty placeholder.
 *
 * Display only. The payload is untrusted device-supplied text; it is
 * encoded into modules and never rendered as text or as a link. If raw
 * text is ever added here, it needs escaping and a scheme allowlist
 * (`javascript:`, `data:`) before it can be linkified.
 */
export function BadgeQrPanel({ cardQr }: BadgeQrPanelProps) {
  const value = typeof cardQr === 'string' ? cardQr.trim() : '';
  if (value === '') return null;
  if (textEncoder.encode(value).length > QR_MAX_BYTES) return null;

  return (
    <Card title="Scanned QR" size="small">
      <div data-testid="badge-qr" style={{ display: 'flex', padding: space.sm }}>
        {/*
          Fixed, opaque colors rather than antd tokens: QR decoding assumes
          dark modules on a light background, and a themed fill inverts to
          light-on-dark under the app's dark theme, which many scanners
          cannot read. The QR must stay dark-on-light in both themes, so
          the colors are hardcoded, not pulled from the theme token.
        */}
        <QRCodeSVG
          value={value}
          size={200}
          level="M"
          marginSize={4}
          bgColor="#FFFFFF"
          fgColor="#0F1240"
          title="QR code scanned from the lead's badge"
        />
      </div>
    </Card>
  );
}
